import {
  Component,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
  inject,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';

/**
 * Tag Input Component
 * Allows users to input multiple values as tags
 * Each tag can be removed by clicking the X button
 * Tags are created when user presses space or on blur
 */
@Component({
  selector: 'app-tag-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputTextModule, TextareaModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagInputComponent),
      multi: true,
    },
  ],
  templateUrl: './tag-input.component.html',
  styleUrl: './tag-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagInputComponent implements ControlValueAccessor {
  @Input() placeholder: string = '';
  @Input() separator: string = ' '; // Default separator is space
  @Input() disabled: boolean = false;
  @Input() addOnBlur: boolean = true;

  @ViewChild('inputRef') inputRef!: ElementRef<HTMLTextAreaElement>;

  tags: string[] = [];
  currentInput: string = '';

  private onChange = (value: string[]) => {};
  private onTouched = () => {};
  private cdr = inject(ChangeDetectorRef);

  /**
   * Focus input field
   */
  focusInput(): void {
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.focus();
    }
  }

  /**
   * Handle input keydown - add tag on separator key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === this.separator || event.key === 'Space') {
      event.preventDefault();
      this.addTagFromInput();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.addTagFromInput();
    } else if (event.key === 'Backspace' && !this.currentInput && this.tags.length > 0) {
      // Remove last tag on backspace when input is empty
      this.removeTag(this.tags.length - 1);
    }
  }

  /**
   * Handle input blur - add tag if has value
   */
  onBlur(): void {
    if (this.addOnBlur) {
      this.addTagFromInput();
    }
    this.onTouched();
  }

  /**
   * Handle input change
   */
  onInputChange(value: string): void {
    this.currentInput = value;
  }

  /**
   * Add tag from current input value
   */
  private addTagFromInput(): void {
    const trimmedValue = this.currentInput.trim();
    if (trimmedValue && !this.tags.includes(trimmedValue)) {
      this.tags = [...this.tags, trimmedValue];
      this.currentInput = '';
      this.onChange(this.tags);
    } else if (trimmedValue) {
      // Clear input even if tag already exists
      this.currentInput = '';
    }
  }

  /**
   * Remove tag at index
   */
  removeTag(index: number): void {
    this.tags = this.tags.filter((_, i) => i !== index);
    this.onChange(this.tags);
  }

  // ControlValueAccessor implementation
  writeValue(value: string[]): void {
    if (Array.isArray(value)) {
      this.tags = [...value];
    } else {
      this.tags = [];
    }
    // Clear current input when value is cleared
    if (!value || value.length === 0) {
      this.currentInput = '';
    }
    // Force change detection to update UI
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

